import React from "react";
import {
  ArrowLeft,
  MessageCircle,
  ShieldCheck,
  Languages,
  FileCheck2,
  Star,
  MapPin,
} from "lucide-react";
import {
  buildWhatsAppLink,
  DEFAULT_WA_MESSAGE,
} from "../lib/constants";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="hero"
      className="relative overflow-hidden hero-grain"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 lg:pt-24 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Text block */}
          <div className="lg:col-span-7 text-start">
            <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-1.5 rounded-full text-sm font-medium mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
              دعم باللغة العربية · استشارة أو خدمة كاملة
            </div>

            <h1 className="font-tajawal text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight text-slate-900">
              قنّن إقامتك في بولندا{" "}
              <span className="text-emerald-600">بدون فوضى</span> وبدون أخطاء
              مكلفة
            </h1>

            <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
              نساعد من يقيم حالياً في بولندا أو يخطط للعيش فيها على اختيار
              مسار الإقامة المناسب، والسير في الإجراءات بوضوح وبأقل المخاطر.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => scrollTo("#lead-form")}
                className="btn-accent rounded-xl px-6 py-3.5 font-semibold inline-flex items-center gap-2 shadow-sm"
                data-testid="hero-primary-cta"
              >
                تحقّق من مسار إقامتك
                <ArrowLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => scrollTo("#offers")}
                className="btn-slate rounded-xl px-6 py-3.5 font-semibold inline-flex items-center gap-2"
                data-testid="hero-secondary-cta"
              >
                احجز استشارة · 100€
              </button>
              <a
                href={buildWhatsAppLink(DEFAULT_WA_MESSAGE)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1DA851] text-white rounded-xl px-6 py-3.5 font-semibold transition-colors shadow-sm"
                data-testid="hero-whatsapp-cta"
              >
                <MessageCircle className="w-4 h-4" />
                واتساب
              </a>
            </div>

            <div className="mt-6 inline-flex items-center gap-3 bg-white border border-slate-200 rounded-full ps-2 pe-4 py-1.5 shadow-sm" data-testid="hero-social-proof">
              <div className="flex -space-x-1 rtl:space-x-reverse">
                <span className="w-7 h-7 rounded-full bg-emerald-700 text-white text-xs flex items-center justify-center font-semibold border-2 border-white">ع</span>
                <span className="w-7 h-7 rounded-full bg-slate-800 text-white text-xs flex items-center justify-center font-semibold border-2 border-white">س</span>
                <span className="w-7 h-7 rounded-full bg-emerald-600 text-white text-xs flex items-center justify-center font-semibold border-2 border-white">ي</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span className="text-sm font-semibold text-slate-800">+100 عميل راضٍ</span>
              </div>
            </div>

            <div
              className="mt-3 flex items-center gap-2 text-xs sm:text-sm text-slate-500"
              data-testid="hero-cities"
            >
              <MapPin className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" />
              <span className="font-medium">نعمل في جميع أنحاء بولندا</span>
              <span className="text-slate-300">·</span>
              <span className="truncate">وارسو · كراكوف · بوزنان · فروتسواف · غدانسك</span>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <TrustPoint
                icon={<Languages className="w-5 h-5" />}
                text="دعم كامل باللغة العربية"
                testid="trust-arabic"
              />
              <TrustPoint
                icon={<FileCheck2 className="w-5 h-5" />}
                text="تقييم واضح لوضعك القانوني"
                testid="trust-assessment"
              />
              <TrustPoint
                icon={<ShieldCheck className="w-5 h-5" />}
                text="استشارة أو خدمة كاملة حسب حاجتك"
                testid="trust-service"
              />
            </div>
          </div>

          {/* Visual block */}
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-br from-emerald-50 via-white to-slate-100 rounded-[2.5rem] blur-xl opacity-60" />
              <div className="relative bg-white border border-slate-200 rounded-[2rem] p-6 sm:p-8 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.15)]">
                <div className="flex items-center justify-between pb-5 border-b border-slate-100">
                  <div>
                    <div className="text-xs text-slate-500 mb-1">
                      تقييم مبدئي للحالة
                    </div>
                    <div className="font-tajawal text-lg font-bold text-slate-900">
                      ملخص مسار الإقامة
                    </div>
                  </div>
                  <div className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2.5 py-1 font-medium">
                    جاهز خلال دقائق
                  </div>
                </div>

                <div className="pt-5 space-y-4">
                  <Row label="الوضع" value="طالب في بولندا" highlight />
                  <Row label="الخطر الأهم" value="اقتراب انتهاء الإقامة" />
                  <Row label="الإجراء الموصى" value="استشارة 100€" />
                  <Row label="المدة المتوقعة" value="يبدأ خلال 48 ساعة" />
                </div>

                <button
                  type="button"
                  onClick={() => scrollTo("#lead-form")}
                  className="mt-6 w-full btn-accent rounded-xl px-5 py-3 font-semibold inline-flex items-center justify-center gap-2"
                  data-testid="hero-card-cta"
                >
                  ابدأ التحقق المجاني
                  <ArrowLeft className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustPoint({ icon, text, testid }) {
  return (
    <div
      className="flex items-center gap-3 bg-white/60 border border-slate-200 rounded-xl px-4 py-3"
      data-testid={testid}
    >
      <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
        {icon}
      </div>
      <span className="text-sm font-medium text-slate-800">{text}</span>
    </div>
  );
}

function Row({ label, value, highlight = false }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-500">{label}</span>
      <span
        className={
          "text-sm font-semibold " +
          (highlight ? "text-emerald-700" : "text-slate-900")
        }
      >
        {value}
      </span>
    </div>
  );
}
